import { useState, useEffect } from "react";
import { getUserStats } from "../services/PostService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const DaysRankingTable = ({ days, startDate: propStartDate, endDate: propEndDate }) => {
  const [loading, setLoading] = useState(false);
  const [consecutiveData, setConsecutiveData] = useState([]);
  const [nonConsecutiveData, setNonConsecutiveData] = useState([]);
  const [error, setError] = useState(null);
  const [downloadingConsecutive, setDownloadingConsecutive] = useState(false);
  const [downloadingNonConsecutive, setDownloadingNonConsecutive] =
    useState(false);
  const navigate = useNavigate();

  // Use startDate from props if provided, otherwise calculate from today
  const startDate = propStartDate 
    ? dayjs(propStartDate).format("YYYY-MM-DD")
    : dayjs().subtract(days - 1, "day").format("YYYY-MM-DD");
  
  // Use endDate from props if provided, otherwise use today
  const endDate = propEndDate
    ? dayjs(propEndDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getUserStats(days, startDate, endDate);

        // Handle response - check status === 1 for success
        if (response.status === 1 && response.data) {
          setConsecutiveData(response.data.consecutive_group?.users || []);
          setNonConsecutiveData(response.data.non_consecutive_group?.users || []);
        } else if (response.status_code === 200 && response.data) {
          // Fallback for old format
          setConsecutiveData(response.data.consecutive_group?.users || []);
          setNonConsecutiveData(response.data.non_consecutive_group?.users || []);
        } else {
          setError(response.message || "Lỗi khi lấy dữ liệu");
        }
      } catch (err) {
        setError("Lỗi khi lấy dữ liệu");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [days, startDate, endDate, propStartDate, propEndDate]);

  const handleUser = (user) => {
    // Use firstName and lastName from API response
    const lastName = user.lastName || "";
    const firstName = user.firstName || "";
    navigate(`/userActivities/${user.id}/${lastName}/${firstName}`);
  };

  const exportToExcel = (data, type) => {
    const workbook = XLSX.utils.book_new();

    const excelData = data.map((user, index) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      return {
        STT: index + 1,
        ID: user.id,
        "Mã KH": user.code,
        "Họ tên": fullName,
        "Số điện thoại": user.phoneNumber,
        "Tổng số ngày đăng": user.total_days,
        "Chuỗi ngày dài nhất": user.max_streak,
        "Các ngày đăng": user.posted_dates ? user.posted_dates.join(", ") : "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const sheetName = type === "consecutive" ? "Liên tiếp" : "Không liên tiếp";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const fileName = `Thống_kê_${days}_ngay_${type === "consecutive" ? "lien_tiep" : "khong_lien_tiep"}_${dayjs().format("DD-MM-YYYY")}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleDownloadConsecutive = () => {
    setDownloadingConsecutive(true);
    try {
      exportToExcel(consecutiveData, "consecutive");
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setTimeout(() => setDownloadingConsecutive(false), 500);
    }
  };

  const handleDownloadNonConsecutive = () => {
    setDownloadingNonConsecutive(true);
    try {
      exportToExcel(nonConsecutiveData, "non-consecutive");
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setTimeout(() => setDownloadingNonConsecutive(false), 500);
    }
  };

  if (loading)
    return (
      <div className="text-center text-gray-500 dark:text-gray-300 italic pt-20">
        Đang tải...
      </div>
    );
  if (error)
    return (
      <div className="text-center text-gray-500 dark:text-gray-300 italic pt-20">
        {error}
      </div>
    );

  return (
    <div className="space-y-8">
      {/* Consecutive Days Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            User đăng đủ {days} ngày liên tiếp
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
              ({consecutiveData.length} người)
            </span>
          </h3>
          <button
            onClick={handleDownloadConsecutive}
            disabled={downloadingConsecutive || consecutiveData.length === 0}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              downloadingConsecutive || consecutiveData.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
            }`}
          >
            {downloadingConsecutive ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Đang tải...
              </div>
            ) : (
              <div className="flex items-center">
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Tải Excel
              </div>
            )}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  STT
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Mã KH
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Họ tên
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Số điện thoại
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Số ngày đăng
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Chi tiết
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {consecutiveData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              ) : (
                consecutiveData.map((user, idx) => (
                  <tr
                    key={user.id || idx}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      {idx + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.total_days}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center cursor-pointer"
                      onClick={() => handleUser(user)}
                    >
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Non-Consecutive Days Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            User đăng đủ {days} ngày không liên tiếp
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
              ({nonConsecutiveData.length} người)
            </span>
          </h3>
          <button
            onClick={handleDownloadNonConsecutive}
            disabled={
              downloadingNonConsecutive || nonConsecutiveData.length === 0
            }
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              downloadingNonConsecutive || nonConsecutiveData.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
            }`}
          >
            {downloadingNonConsecutive ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Đang tải...
              </div>
            ) : (
              <div className="flex items-center">
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Tải Excel
              </div>
            )}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  STT
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Mã KH
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Họ tên
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Số điện thoại
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Số ngày đăng
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Chi tiết
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {nonConsecutiveData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              ) : (
                nonConsecutiveData.map((user, idx) => (
                  <tr
                    key={user.id || idx}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      {idx + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {user.total_days}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center cursor-pointer"
                      onClick={() => handleUser(user)}
                    >
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DaysRankingTable;

